import { Arrangement, DEFAULT_ARRANGEMENT, RateUnit, Trade, } from "@gigsync/protocol";
import { areaOfSlug } from "../estimate/areas.js";
import { priceText } from "../i18n/reasons.js";
import { pluralKey, t } from "../i18n/t.js";
import { Pick } from "./card.js";
import { isMessageKey } from "./choices.js";
import { blendLine, driverLines } from "./estimate-drivers.js";
const RANGE = {
    [RateUnit.enum.job]: "estimate.range_job",
    [RateUnit.enum.month]: "estimate.range_month",
    [RateUnit.enum.visit]: "estimate.range_visit",
    [RateUnit.enum.day]: "estimate.range_day",
    [RateUnit.enum.hour]: "estimate.range_hour",
    [RateUnit.enum.sqft]: "estimate.range_sqft",
    [RateUnit.enum.week]: "estimate.range_week",
};
const OTHER_PROBLEM = "other";
const T = Trade.enum;
/** The scope's buckets in the reader's words: "2 BHK, labour only", "3–4 people, 2 meals a day". */
function scopeParts(own, request) {
    const keys = [];
    // ADR 0046: by the hour, a maid's flat does not move the price and a driver's day is not the job; a visit or a
    // week is still for the flat's rooms. "Every day" and "hours a day" are monthly scope.
    const other = request.arrangement !== undefined && request.arrangement !== DEFAULT_ARRANGEMENT;
    if (other && (request.trade === T.driver || request.arrangement === Arrangement.enum.hourly))
        return [];
    if (other && request.trade === T.maid)
        return [t(own, `estimate.rooms.${request.scope.rooms}`)];
    switch (request.trade) {
        case T.painter:
            if (request.scope.rooms)
                keys.push(`estimate.rooms.${request.scope.rooms}`);
            keys.push(`estimate.labour.${request.scope.labour_only}`);
            break;
        case T.maid:
            keys.push(`estimate.rooms.${request.scope.rooms}`, `estimate.frequency.${request.scope.frequency}`);
            break;
        case T.cook:
            keys.push(`estimate.people.${request.scope.people}`, `estimate.meals.${request.scope.meals}`);
            break;
        case T.driver:
            keys.push(`estimate.hours.${request.scope.hours}`);
            break;
        default:
            if (request.scope.problem !== OTHER_PROBLEM)
                keys.push(`estimate.problem.${request.scope.problem}`);
    }
    return keys.map((k) => t(own, k));
}
/** Where the range is for: the area its GigSync jobs are in, else the city. */
function placeKeys({ request, estimate }) {
    const area = estimate.basis.area && areaOfSlug(estimate.basis.area);
    const key = area ? `area.${area.key}` : `city.${request.city}`;
    return isMessageKey(key) ? [key] : [];
}
/** What the range rests on: every source it blends, or, for a range from before the blend, GigSync's jobs and where. */
function basisLine(own, found, place) {
    const { estimate } = found;
    if (estimate.basis.blend)
        return blendLine(own, found.request, estimate);
    const jobs = estimate.basis.gigsync_jobs;
    return { key: pluralKey(own, "estimate.jobs", jobs), params: { count: jobs }, names: { place } };
}
/** The range with its scope and place, then what it rests on, and the legal minimum if known. */
export function estimateLines(own, found) {
    const { request, estimate } = found;
    const parts = scopeParts(own, request);
    const place = placeKeys(found);
    return [
        {
            key: RANGE[estimate.unit],
            names: { trade: [`trade.${request.trade}`], ...(place.length > 0 ? { city: place } : {}) },
            params: { scope: parts.length > 0 ? t(own, "estimate.scope", { scope: parts.join(", ") }) : "" },
            prices: { low: estimate.low_paise, high: estimate.high_paise },
        },
        basisLine(own, found, place),
        ...driverLines(estimate),
        ...estimateLegalLines(found),
    ];
}
/** The legal minimum the estimate was floored at, as a line, when one is known. */
export function estimateLegalLines({ estimate }) {
    const law = estimate.basis.legal_minimum_paise;
    return law ? [{ key: "estimate.legal_min", prices: { min: law } }] : [];
}
/** The amount suggested, said under the question, and the chip that uses it. */
export function suggestion(key, paise) {
    return {
        line: { key, prices: { price: paise } },
        choice: { value: Pick.suggested, key: "estimate.use", params: { price: priceText(paise) } },
    };
}
//# sourceMappingURL=estimate-lines.js.map